import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import './AuthForm.css';
import { FaArrowLeft } from 'react-icons/fa6';
import Preloader from './Preloader';

function MissingReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('pending');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
      document.title = "Missing Report Details";
    }, []);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();
    fetchReport();
    // eslint-disable-next-line
  }, [id]);

  async function fetchReport() {
    setLoading(true);
    setError('');
    const { data, error } = await supabase
      .from('missing_reports')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      setError('Failed to load report');
      setReport(null);
    } else {
      setReport(data);
      setStatus(data.status);
    }
    setLoading(false);
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg('');
    const { error } = await supabase
      .from('missing_reports')
      .update({ status: status })
      .eq('id', id);
    if (error) {
      setSaveMsg('Failed to save changes.');
    } else {
      setSaveMsg('Status updated!');
      // Audit log for edit/archive
      const actionType = status === 'Archived' ? 'archived missing report' : 'edited missing report';
      const { error: auditError } = await supabase.from('audit_logs').insert([{
        user_id: currentUser.id,
        action: actionType,
        target_id: id,
        target_type: 'MissingReports',
        details: {
          name: report.name,
          category: report.category,
          status: status,
          contact: report.contact,
          lastseen: report.lastseen
        }
      }]);
      // No debug logging
      fetchReport();
    }
    setSaving(false);
  }

  if (loading || !currentUser) return <Preloader text="Loading Details..." />;;
  if (error || !report) return <div style={{ color: '#d32f2f', padding: 40 }}>{error || 'Report not found.'}</div>;

  const canEdit = currentUser && (
    currentUser.role === 'admin' ||
    currentUser.role === 'super admin' ||
    (currentUser.role === 'barangay' && report.user_id === currentUser.user_id)
  );

  const imageUrls = [report.image1_url, report.image2_url, report.image3_url, report.image4_url].filter(Boolean);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <button
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              fontSize: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaArrowLeft />
          </button>
            <div className="title-text">Missing Report Details</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        {/* Card */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 32, boxShadow: '0 2px 8px #0001' }}>
          {/* Title row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h1 style={{ fontSize: 24, margin: 0 }}>{report.name}</h1>
            {canEdit ? (
            <div>
              <label style={{ color: '#666', marginRight: 8 }}>Status:</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
              >
                  <option value="Missing">Missing</option>
                <option value="Found">Found</option>
                <option value="Archived">Archived</option>
              </select>
            </div>
            ) : (
              <div>
                <label style={{ color: '#666', marginRight: 8 }}>Status:</label>
                <span style={{ fontSize: 16, fontWeight: 'bold' }}>{report.status}</span>
              </div>
            )}
          </div>
          {/* Main content: two-column layout */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32 }}>
            {/* Left column: Images */}
            <div style={{ flex: '2 1 500px', minWidth: 300 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center' }}>
                {imageUrls.length > 0 ? (
                  imageUrls.map((img, i) => (
              <img
                key={i}
                      src={img}
                      alt={`report-img-${i + 1}`}
                style={{
                        flex: '1 1 auto',
                        maxWidth: imageUrls.length > 1 ? 'calc(50% - 8px)' : '100%',
                        height: 'auto',
                        aspectRatio: '1 / 1',
                  borderRadius: 12,
                  objectFit: 'cover',
                  background: '#eee',
                      }}
                    />
                  ))
                ) : (
                  <div style={{
                    width: '100%',
                    height: 300,
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f0f0f0',
                    color: '#888'
                  }}>
                    No Images Provided
                  </div>
                )}
              </div>
          </div>
            {/* Right column: Details */}
            <div style={{ flex: '1 1 300px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Category</label>
              <span style={{ fontSize: 16}}>
                {report.category || 'Person'}
              </span>
            </div>
            <div>
              <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Age</label>
              <span style={{ fontSize: 16}}>{report.age || 'N/A'}</span>
            </div>
            <div>
              <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Gender</label>
              <span style={{ fontSize: 16}}>{report.gender || 'N/A'}</span>
            </div>
            <div>
              <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Contact</label>
              <span style={{ fontSize: 16}}>{report.contact || 'N/A'}</span>
            </div>
            <div>
              <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Date Missing</label>
              <span style={{ fontSize: 16}}>{report.dateMissing || 'N/A'}</span>
            </div>
            <div>
              <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Reported On</label>
              <span style={{ fontSize: 16 }}>{new Date(report.created_at).toLocaleString()}</span>
            </div>
            <div>
              <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Last Seen</label>
              <span style={{ fontSize: 16 }}>{report.lastseen}</span>
            </div>
            <div>
              <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Details</label>
                  <p style={{ fontSize: 16, margin: 0, lineHeight: 1.5 }}>{report.details}</p>
            </div>
            {/* Action buttons */}
                {canEdit && (
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={handleSave}
                style={{
                  background: '#19c37d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 24px',
                  fontWeight: 'bold',
                  fontSize: 16
                }}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => navigate(-1)}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: '1px solid #ccc',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: 16
                }}
              >
                Cancel
              </button>
            </div>
                )}
            {saveMsg && (
              <div style={{ color: saveMsg.includes('Failed') ? '#d32f2f' : '#19c37d', marginTop: 12 }}>
                {saveMsg}
              </div>
            )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MissingReportDetail;
